import { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { AlertTriangle, Shield, ShieldAlert, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function NoShowRiskBadge({ 
  reservationId, 
  prediction = null,
  showDetails = true,
  size = 'default' // 'small', 'default', 'large'
}) {
  const [risk, setRisk] = useState(prediction);
  const [loading, setLoading] = useState(!prediction);

  useEffect(() => {
    if (!prediction && reservationId) {
      fetchPrediction();
    }
  }, [reservationId, prediction]);

  const fetchPrediction = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/predictions/no-show/${reservationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRisk(data.prediction);
      }
    } catch (err) {
      console.error('Error fetching prediction:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="w-6 h-6 rounded-full bg-muted animate-pulse" />;
  }

  if (!risk) return null;

  const getIconSize = () => {
    if (size === 'small') return 'h-3 w-3';
    if (size === 'large') return 'h-5 w-5';
    return 'h-4 w-4';
  };

  const getIcon = () => {
    const iconSize = getIconSize();
    switch (risk.risk_level) {
      case 'high':
        return <ShieldAlert className={cn("text-red-500", iconSize)} />;
      case 'medium':
        return <AlertTriangle className={cn("text-yellow-500", iconSize)} />;
      case 'low':
        return <Shield className={cn("text-blue-500", iconSize)} />;
      default:
        return <ShieldCheck className={cn("text-green-500", iconSize)} />;
    }
  };

  const getBadgeSize = () => {
    if (size === 'small') return 'px-1.5 py-0.5 text-[10px]';
    if (size === 'large') return 'px-3 py-1.5 text-sm';
    return 'px-2 py-1 text-xs';
  };

  const getBadgeStyles = () => {
    const baseStyles = cn(
      "inline-flex items-center gap-1 rounded-full font-medium",
      getBadgeSize()
    );
    
    switch (risk.risk_level) {
      case 'high':
        return cn(baseStyles, "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400");
      case 'medium':
        return cn(baseStyles, "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400");
      case 'low':
        return cn(baseStyles, "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400");
      default:
        return cn(baseStyles, "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400");
    }
  };

  const badge = (
    <div className={getBadgeStyles()} data-testid="no-show-risk-badge">
      {getIcon()}
      {size !== 'small' && <span>{risk.percentage}%</span>}
    </div>
  );

  if (!showDetails) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="max-w-xs p-3"
          data-testid="no-show-risk-tooltip"
        >
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-medium">{risk.risk_label}</span>
              <span className="text-lg font-bold">{risk.percentage}%</span>
            </div>
            
            {risk.factors && risk.factors.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground font-medium">Factores:</p>
                {risk.factors.map((factor, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className={cn(
                      "w-2 h-2 rounded-full",
                      factor.impact === 'high' ? 'bg-red-500' :
                      factor.impact === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                    )} />
                    <span>{factor.factor}</span>
                  </div>
                ))}
              </div>
            )}
            
            {risk.recommendations && risk.recommendations.length > 0 && (
              <div className="pt-2 border-t border-border">
                <p className="text-xs text-muted-foreground font-medium mb-1">Recomendaciones:</p>
                {risk.recommendations.map((rec, i) => (
                  <p key={i} className="text-xs">• {rec}</p>
                ))}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function NoShowRiskStats({ stats }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="no-show-stats">
      <div className="p-4 bg-muted rounded-xl">
        <p className="text-xs text-muted-foreground">Total No-Shows</p>
        <p className="text-2xl font-bold">{stats?.total_no_shows || 0}</p>
        <p className="text-xs text-muted-foreground">de {stats?.total_reservations || 0} reservas</p>
      </div>
      <div className="p-4 bg-muted rounded-xl">
        <p className="text-xs text-muted-foreground">Tasa General</p>
        <p className="text-2xl font-bold">{stats?.overall_rate || 0}%</p>
      </div>
      <div className="p-4 bg-muted rounded-xl">
        <p className="text-xs text-muted-foreground">No-Shows Este Mes</p>
        <p className="text-2xl font-bold">{stats?.month_no_shows || 0}</p>
        <p className="text-xs text-muted-foreground">de {stats?.month_reservations || 0} reservas</p>
      </div>
      <div className="p-4 bg-muted rounded-xl">
        <p className="text-xs text-muted-foreground">Tasa Mensual</p>
        <p className={cn(
          "text-2xl font-bold",
          (stats?.month_rate || 0) > 10 ? "text-red-500" :
          (stats?.month_rate || 0) > 5 ? "text-yellow-500" : "text-green-500"
        )}>
          {stats?.month_rate || 0}%
        </p>
      </div>
    </div>
  );
}
